import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock3, Mail, MapPin, Phone } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';
import CountUp from './CountUp';
import GradientText from './GradientText';
import { BACKEND_URL, api } from '../lib/api';

function getBlockValue(block, key, lang) {
  if (lang === 'en' && block[`${key}_en`]) {
    return block[`${key}_en`];
  }
  return block[key];
}

function FooterStats({ block, lang }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  const countUpEnabled = block.countup_enabled !== false;
  const countUpDuration = Number(block.countup_duration ?? 1);
  const countUpDelay = Number(block.countup_delay ?? 0);
  const countUpDirection = block.countup_direction || 'up';
  const countUpSeparator = block.countup_separator ?? '';
  const countUpStartWhen = block.countup_start_when !== false;
  const gradientEnabled = block.gradient_enabled !== false;
  const gradientFrom = block.gradient_from || '#E11D2E';
  const gradientTo = block.gradient_to || '#FFFFFF';
  const gradientAngle = Number(block.gradient_angle ?? 90);

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item, index) => {
        const label = getBlockValue(item, 'label', lang);
        const suffix = getBlockValue(item, 'suffix', lang) || '';
        const rawValue = item.value ?? '';
        const numericValue = Number(rawValue);
        const shouldCount = countUpEnabled && Number.isFinite(numericValue);
        const valueContent = shouldCount ? (
          <CountUp
            from={0}
            to={numericValue}
            direction={countUpDirection}
            duration={Number.isFinite(countUpDuration) ? countUpDuration : 1}
            delay={Number.isFinite(countUpDelay) ? countUpDelay : 0}
            separator={countUpSeparator}
            startWhen={countUpStartWhen}
            className="count-up-text"
          />
        ) : (
          <span className="count-up-text">{rawValue}</span>
        );
        const valueWithSuffix = (
          <>
            {valueContent}
            {suffix}
          </>
        );
        return (
          <div key={`${item.value}-${index}`} className="glass rounded-2xl p-4 text-center space-y-2">
            {gradientEnabled ? (
              <GradientText
                className="text-2xl md:text-3xl font-black font-heading"
                from={gradientFrom}
                to={gradientTo}
                angle={gradientAngle}
              >
                {valueWithSuffix}
              </GradientText>
            ) : (
              <div className="text-2xl md:text-3xl font-black font-heading text-white">
                {valueWithSuffix}
              </div>
            )}
            {label && <div className="text-white/60 text-sm">{label}</div>}
          </div>
        );
      })}
    </section>
  );
}

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { settings } = useSiteSettings();
  const [footerStats, setFooterStats] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchFooterStats = async () => {
      try {
        const response = await api.get('/pages-dynamic/home');
        const statsBlock = (response.data?.blocks || []).find((block) => block?.type === 'stats');
        if (isMounted) {
          setFooterStats(statsBlock || null);
        }
      } catch (error) {
        console.error('Failed to fetch footer stats', error);
      }
    };
    fetchFooterStats();
    return () => {
      isMounted = false;
    };
  }, []);

  const companyLinks = [
    { path: '/about', label: t('nav.about') },
    { path: '/cases', label: t('nav.cases') },
    { path: '/insights', label: t('nav.insights') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const serviceLinks = [
    { path: '/services/trade-manufacturing', label: t('services.trade.name') },
    { path: '/services/logistics-qc', label: t('services.logistics.name') },
    { path: '/services/corporate-travel-events', label: t('services.travel.name') },
    { path: '/services/investments-partnerships', label: t('services.investments.name') },
  ];

  const resourceLinks = [
    { path: '/invest', label: t('nav.invest') },
    { path: '/partners', label: t('nav.partners') },
    { path: '/events', label: t('nav.events') },
    { path: '/download', label: t('common.download_presentation') },
  ];

  const legalLinks = [
    { path: '/privacy', label: t('footer.privacy') },
    { path: '/terms', label: t('footer.terms') },
    { path: '/nda', label: t('footer.nda') },
  ];

  const sections = settings?.footer?.sections?.length
    ? settings.footer.sections.map((section) => ({
      title: i18n.language === 'en' && section.title_en ? section.title_en : section.title,
      links: (section.links || []).map((link) => ({
        ...link,
        label: i18n.language === 'en' && link.label_en ? link.label_en : link.label,
      })),
    }))
    : [
      { title: t('footer.company'), links: companyLinks },
      { title: t('footer.services'), links: serviceLinks },
      { title: t('footer.resources'), links: resourceLinks },
      { title: t('footer.legal'), links: legalLinks },
    ];

  const defaultSocials = [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/aichin-group/' },
    { label: 'Telegram', href: 'https://t.me/aichinorg' },
  ];
  const configuredSocials = Array.isArray(settings?.footer?.socials) ? settings.footer.socials : [];
  const defaultKeys = new Set(defaultSocials.map((social) => (social.label || social.href || '').toLowerCase()));
  const configuredByKey = new Map(
    configuredSocials.map((social) => [(social.label || social.href || '').toLowerCase(), social]),
  );
  const socials = [
    ...defaultSocials.map((social) => {
      const key = (social.label || social.href || '').toLowerCase();
      return configuredByKey.get(key) || social;
    }),
    ...configuredSocials.filter((social) => {
      const key = (social.label || social.href || '').toLowerCase();
      return !defaultKeys.has(key);
    }),
  ];

  const address = i18n.language === 'en' && settings?.footer?.address_en
    ? settings.footer.address_en
    : settings?.footer?.address || t('footer.address');
  const email = settings?.footer?.email || 'info@aichin.group';
  const phone = settings?.footer?.phone;
  const workingHours = i18n.language === 'en' && settings?.footer?.working_hours_en
    ? settings.footer.working_hours_en
    : settings?.footer?.working_hours;

  return (
    <footer className="relative bg-[#0A0A0A] border-t border-white/10 overflow-hidden" data-testid="main-footer">
      {/* Massive background text */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
        <span className="font-heading font-black text-[15vw] leading-none">AICHIN</span>
      </div>

      {footerStats?.items?.length ? (
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
            <FooterStats block={footerStats} lang={i18n.language} />
          </div>
        </div>
      ) : null}

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="font-heading font-bold text-white mb-6">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href || link.path}>
                    {link.href?.startsWith('http') ? (
                      <a
                        href={link.href}
                        className="text-sm text-white/60 hover:text-[#E11D2E] transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href || link.path}
                        className="text-sm text-white/60 hover:text-[#E11D2E] transition-colors"
                        data-testid={`footer-link-${(link.href || link.path || '').replace('/', '')}`}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-white mb-6">{t('footer.contact_title')}</h3>
            <ul className="space-y-3">
              {address ? (
                <li className="flex items-start space-x-2 text-sm text-white/60">
                  <MapPin size={16} className="mt-1 flex-shrink-0" />
                  <span>{address}</span>
                </li>
              ) : null}
              <li className="flex items-start space-x-2">
                <Mail size={16} className="mt-1 flex-shrink-0 text-white/60" />
                <a
                  href={`mailto:${email}`}
                  className="text-sm text-[#E11D2E] hover:underline"
                  data-testid="footer-email-link"
                >
                  {email}
                </a>
              </li>
              {phone && (
                <li className="flex items-start space-x-2 text-sm text-white/60">
                  <Phone size={16} className="mt-1 flex-shrink-0" />
                  <span>{phone}</span>
                </li>
              )}
              {workingHours && (
                <li className="flex items-start space-x-2 text-sm text-white/60">
                  <Clock3 size={16} className="mt-1 flex-shrink-0" />
                  <span>{workingHours}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-sm text-white/40">{t('footer.rights')}</p>
          <div className="flex items-center space-x-4">
            {socials.map((social) => (
              <a
                key={social.href}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#E11D2E] transition-colors"
              >
                {social.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
